import React from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useVideoCall } from '../../contexts/VideoCallContext';
import { meetingService } from '../../services/meetingService';
import videoService from '../../services/videoService';

interface MeetingActionBarProps {
  // Meeting data
  meeting?: {
    id?: number;
    team_url?: string;
    status?: string;
    isPSYMeeting?: boolean;
    startsRecallBot?: boolean;
  };

  // Action handlers (callbacks)
  onSave: () => Promise<void> | void;
  onFinish?: () => Promise<void> | void;
  onNoShow: () => void;
  onFinishTreatment?: () => void;
  onSendMeetingLink?: () => void;

  // Loading states
  isSaving?: boolean;
  isFinishing?: boolean;

  // Button customization
  saveButtonText?: string;
  finishButtonText?: string;
  /** Label while the finish action is in flight; not every type summarizes. */
  finishingButtonText?: string;
  showFinishButton?: boolean;

  // Additional buttons to show in the center section
  additionalButtons?: React.ReactNode;

  // Optional right-side content
  rightContent?: React.ReactNode;

  // Custom styling
  className?: string;
}

export const MeetingActionBar: React.FC<MeetingActionBarProps> = ({
  meeting,
  onSave,
  onFinish,
  onNoShow,
  onFinishTreatment,
  onSendMeetingLink,
  isSaving = false,
  isFinishing = false,
  saveButtonText = 'Save',
  finishButtonText = 'Finish & Summarize',
  finishingButtonText = 'Summarizing...',
  showFinishButton = true,
  additionalButtons,
  rightContent,
  className = '',
}) => {
  const { user } = useAuth();
  const { startCall } = useVideoCall();

  // One privilege governs everything video: these buttons, the /video-rooms page
  // and its sidebar entry. Previously this rode on manage_users, which coupled
  // running a session to administering accounts - unrelated concerns.
  const canUseVideo = Boolean(user?.user_privilege?.view_video_rooms);

  // Opens the floating call window rather than navigating: the therapist stays on
  // this page and keeps working while the session runs. Starting is idempotent
  // server-side, so clicking twice re-enters the same room.
  const handleVideoRoomClick = () => {
    if (!meeting?.id) return;
    startCall(meeting.id);
  };

  // Copying the link deliberately does not open a room: the join link depends only
  // on the meeting, so it can be sent to the patient well before the session.
  const handleCopyPatientLink = async () => {
    if (!meeting?.id) return;
    try {
      const vm = await videoService.ensureVideoMeeting({ externalId: meeting.id });
      try {
        await navigator.clipboard.writeText(vm.join_url);
        toast.success('Patient link copied');
      } catch {
        window.prompt('Copy the patient link:', vm.join_url);
      }
    } catch (error) {
      console.error('Failed to get the patient video link:', error);
      toast.error('Could not get the patient link');
    }
  };

  const handleTeamLinkClick = async () => {
    if (!meeting?.team_url) return;

    // For meetings that record via Recall (PSY + CM), notify server to create a Recall bot
    if ((meeting.startsRecallBot ?? meeting.isPSYMeeting) && meeting.id) {
      try {
        await meetingService.startMeeting(meeting.id);
      } catch (e) {
        // Non-blocking — open the URL regardless
        console.error('Failed to start Recall bot:', e);
      }
    }

    window.open(meeting.team_url, '_blank');
  };

  const shouldShowFinish = showFinishButton && meeting?.status !== 'done' && onFinish;

  return (
    <div className={`flex items-center gap-4 mt-1 pt-2 pb-2 border-t border-gray-300 ${className}`}>
      {/* Left: Team Link and No Show Icons */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleTeamLinkClick}
          className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
          title="Team Link"
          disabled={!meeting?.team_url}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.658 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
        {canUseVideo && (
          <>
            <button
              onClick={handleVideoRoomClick}
              className="p-2 text-gray-600 hover:text-[#3A7380] transition-colors"
              title="Open the video room"
              disabled={!meeting?.id}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={handleCopyPatientLink}
              className="p-2 text-gray-600 hover:text-[#3A7380] transition-colors"
              title="Copy the patient join link"
              disabled={!meeting?.id}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2M10 8h8a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8a2 2 0 012-2z" />
              </svg>
            </button>
          </>
        )}
        {meeting?.status !== 'noshow' && (
          <button
            onClick={onNoShow}
            className="p-2 text-red-600 hover:text-red-700 transition-colors text-xl"
            title="No Show"
          >
            ⛔
          </button>
        )}
        {onFinishTreatment && (
          <button
            onClick={onFinishTreatment}
            className="p-2 text-red-600 hover:text-red-700 transition-colors"
            title="Finish Treatment"
          >
            <svg className="w-6 h-6" fill="#e23232" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
            </svg>
          </button>
        )}
        {onSendMeetingLink && (
          <button
            onClick={onSendMeetingLink}
            className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
            title="Send Meeting Link"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="5" width="18" height="14" rx="2" ry="2" strokeWidth="2" />
              <path d="M3 7l9 6 9-6" strokeWidth="2" />
              <circle cx="17" cy="16" r="3" fill="currentColor" stroke="white" strokeWidth="1.5" />
              <path d="M15.5 16h3M17 14.5v3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex-1"></div>

      {/* Center: Save and Finish Buttons */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onSave}
          disabled={isSaving || isFinishing}
          className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : saveButtonText}
        </button>

        {shouldShowFinish && (
          <button
            onClick={onFinish}
            disabled={isSaving || isFinishing}
            className="px-4 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFinishing ? finishingButtonText : finishButtonText}
          </button>
        )}

        {/* Additional buttons */}
        {additionalButtons}
      </div>

      <div className="flex-1"></div>

      {/* Right: Optional content */}
      {rightContent && (
        <div className="flex-shrink-0">
          {rightContent}
        </div>
      )}
    </div>
  );
};
